import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function ResendVerification() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);

  // Check if email was passed from registration
  React.useEffect(() => {
    const storedEmail = sessionStorage.getItem('verificationEmail');
    if (storedEmail) {
      setEmail(storedEmail);
      setShowCodeInput(true); // Show code input directly
      sessionStorage.removeItem('verificationEmail'); // Clear it
    }
  }, []);

  const handleResendCode = async (e) => {
    e.preventDefault();
    setError('');
    setResendingCode(true);

    try {
      const { resendSignUpCode } = await import('aws-amplify/auth');
      
      await resendSignUpCode({
        username: email
      });

      setShowCodeInput(true);
      setError('');
      alert('✅ Verification code sent! Check your email.');
    } catch (err) {
      console.error('Resend code error:', err);
      if (err.name === 'InvalidParameterException' || err.message?.includes('already confirmed')) {
        setError('This account is already verified. Please try logging in.');
      } else if (err.name === 'UserNotFoundException') {
        setError('No account found with this email. Please check your email or register first.');
      } else {
        setError(err.message || 'Failed to resend code. Please try again.');
      }
    } finally {
      setResendingCode(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { confirmSignUp } = await import('aws-amplify/auth');
      
      await confirmSignUp({
        username: email,
        confirmationCode: verificationCode
      });

      setSuccess(true);
    } catch (err) {
      console.error('Verification error:', err);
      if (err.name === 'CodeMismatchException') {
        setError('Invalid verification code. Please check the code and try again.');
      } else if (err.name === 'ExpiredCodeException') {
        setError('Verification code has expired. Please click "Resend Code" to get a new one.');
      } else {
        setError(err.message || 'Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="card" style={{ maxWidth: '600px', margin: '60px auto', textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
        <h2 style={{ color: '#10b981', marginBottom: '20px' }}>Email Verified Successfully!</h2>
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px', lineHeight: '1.6' }}>
          Your account <strong>{email}</strong> has been verified!
        </p>
        <div style={{ 
          background: '#d1fae5', 
          border: '1px solid #10b981',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <p style={{ fontSize: '14px', color: '#065f46', margin: 0 }}>
            <strong>✨ You're all set!</strong> You can now login and access 3 free practice tests per subject plus 4 free topic-based tests across all subjects.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary"
            style={{ padding: '12px 32px' }}
          >
            Go to Login
          </button>
          <button
            onClick={() => navigate('/')}
            className="btn-secondary"
            style={{ padding: '12px 32px' }}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '600px', margin: '60px auto' }}>
      <div className="card" style={{ padding: '40px' }}>
        <h1 style={{ fontSize: '36px', marginBottom: '10px', color: '#4f46e5', textAlign: 'center' }}>
          Verify Your Email
        </h1>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '30px' }}>
          {showCodeInput ? 'Enter the verification code sent to your email' : 'Enter your email to receive a verification code'}
        </p>

        {error && (
          <div style={{
            padding: '15px',
            background: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {!showCodeInput ? (
          // Step 1: Enter email and request code
          <form onSubmit={handleResendCode}>
            <div style={{ 
              background: '#f0f9ff', 
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <p style={{ fontSize: '14px', color: '#1e40af', marginBottom: '10px', fontWeight: '600' }}>
                📧 What This Does
              </p>
              <p style={{ fontSize: '14px', color: '#1e40af', lineHeight: '1.6', margin: 0 }}>
                We'll send a new 6-digit verification code to your email. Use this if you closed the registration page or didn't receive the original code.
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                Email Address *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
                placeholder="your.email@example.com"
              />
              <small style={{ color: '#666', fontSize: '13px', display: 'block', marginTop: '5px' }}>
                💡 Enter the email address you used to register
              </small>
            </div>

            <button
              type="submit"
              disabled={resendingCode}
              className="btn-primary"
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '18px',
                fontWeight: 'bold',
                opacity: resendingCode ? 0.6 : 1,
                cursor: resendingCode ? 'not-allowed' : 'pointer'
              }}
            >
              {resendingCode ? 'Sending Code...' : 'Send Verification Code'}
            </button>
          </form>
        ) : (
          // Step 2: Enter verification code
          <form onSubmit={handleVerifyCode}>
            <div style={{ 
              background: '#d1fae5', 
              border: '1px solid #10b981',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '14px', color: '#065f46', margin: 0 }}>
                ✅ Ready to verify <strong>{email}</strong>
              </p>
            </div>

            <div style={{ 
              background: '#f0f9ff', 
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <p style={{ fontSize: '14px', color: '#1e40af', marginBottom: '8px', fontWeight: '600' }}>
                📧 Have your verification code?
              </p>
              <p style={{ fontSize: '13px', color: '#1e40af', lineHeight: '1.6', margin: 0 }}>
                Enter the 6-digit code from your email below. If you don't have it, click "Resend Code" to get a new one.
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                Verification Code *
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
                maxLength={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '24px',
                  textAlign: 'center',
                  letterSpacing: '8px',
                  fontWeight: 'bold'
                }}
                placeholder="000000"
              />
              <small style={{ color: '#666', fontSize: '13px', display: 'block', marginTop: '5px' }}>
                💡 Enter the 6-digit code from your email
              </small>
            </div>

            <button
              type="submit"
              disabled={loading || verificationCode.length !== 6}
              className="btn-primary"
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '18px',
                fontWeight: 'bold',
                marginBottom: '10px',
                opacity: (loading || verificationCode.length !== 6) ? 0.6 : 1,
                cursor: (loading || verificationCode.length !== 6) ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Verifying...' : 'Verify Email'}
            </button>

            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendingCode}
              className="btn-secondary"
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px'
              }}
            >
              {resendingCode ? 'Sending...' : 'Resend Code'}
            </button>
          </form>
        )}

        <p style={{ textAlign: 'center', marginTop: '20px', color: '#666' }}>
          Already verified?{' '}
          <a href="/login" style={{ color: '#4f46e5', fontWeight: 'bold', textDecoration: 'none' }}>
            Login here
          </a>
        </p>
      </div>

      <div className="card" style={{ marginTop: '20px', padding: '20px', background: '#f0f9ff', borderLeft: '4px solid #3b82f6' }}>
        <p style={{ fontSize: '14px', color: '#1e40af', margin: 0 }}>
          <strong>💡 Tip:</strong> Check your spam folder if you don't see the email. Still having issues? Contact us at{' '}
          <a href="mailto:prepsmart.help@gmail.com" style={{ color: '#4f46e5', fontWeight: 'bold' }}>
            prepsmart.help@gmail.com
          </a>
        </p>
        <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
          💡 Verification emails are sent from <strong>noreply@prepsmart.com.au</strong>
        </p>
      </div>
    </div>
  );
}

export default ResendVerification;
